// import { Database } from 'firebase-firestore-lite'
import cloneDeep from 'clone-deep'
// import Transform from 'firebase-firestore-lite/dist/Transform'
import { isEqual } from 'lodash'
import { debounce } from '../debounce'
import jitsu from '../jitsu'
import { TEST_MODE, localStorageGet, localStorageSet } from '../utils'
// import notify from '../notify'
import { trackUserData } from './savvyAnalytics'

const CAN_USE_LOCAL_STORAGE = storageAvailable()

interface WriteUserDataOptions {
  bypassDebounce?: boolean
}

//LUCIAN added this for debouncing the jitsu data events
const jitsuDebouncer = debounce(function (data: object) {
  jitsu.identify(data)
}, 500) as (data: object) => void
//End of LUCIANs addition

export default async function writeUserData(
  self: any,
  userData: Record<string, unknown>,
  options: WriteUserDataOptions = {}
) {
  const { bypassDebounce = false } = options
  self.entryId = self.entryId || userData?.entryId || '_' + ('' + Math.random()).slice(2)

  const { local: localUserData, firebase: remoteUserData } = prepareUserData(self, userData)
  /* LocalStorage section */
  writeToLocal(self, localUserData)

  const userIds = Array.from(
    new Set(
      Object.entries((CAN_USE_LOCAL_STORAGE && localStorage) || {})
        .filter(([key]) => typeof key === 'string' && key.startsWith('SavvyUserId'))
        .map(([, value]) => value)
    )
  ) as string[]

  /* Analytics Section */
  writeToAnalytics(self, remoteUserData, userIds[0], { bypassDebounce })
  /* Firebase section */
  // writeToFirebase(self, remoteUserData, userIds)
}

function writeToLocal(self: any, userData: Record<string, unknown>) {
  if (CAN_USE_LOCAL_STORAGE) {
    if (userData && Object.keys(userData).length) {
      const allUserDatas = JSON.parse(localStorageGet('SavvyFormUserData') || '{}')
      if (!isEqual(allUserDatas[self.idValue], userData)) {
        allUserDatas[self.idValue] = userData
        localStorageSet('SavvyFormUserData', JSON.stringify(allUserDatas))
      }
    }
  }
}

function writeCloudDataToLocal(self: any, userData: Record<string, unknown>) {
  // Used so that we can send the whole user data for all flow IDs to analytics,
  // without sending keys that are stored locally but shouldn't be sent to the cloud
  if (CAN_USE_LOCAL_STORAGE) {
    if (userData && Object.keys(userData).length) {
      const allUserDatas = JSON.parse(localStorageGet('SavvyFormCloudUserData') || '{}')
      if (!isEqual(allUserDatas[self.idValue], userData)) {
        allUserDatas[self.idValue] = userData
        localStorageSet('SavvyFormCloudUserData', JSON.stringify(allUserDatas))
      }
    }
  }

  return JSON.parse(localStorageGet('SavvyFormCloudUserData') || '{}')
}

function writeToAnalytics(
  self: any,
  userData: Record<string, unknown>,
  userIds: unknown,
  options: WriteUserDataOptions = {}
) {
  const { bypassDebounce = false } = options
  const allData = writeCloudDataToLocal(self, userData)

  const pulledOutDataKeys = [
    'email',
    'name',
    'first_name',
    'last_name',
    'nickname',
    'username',
    'phone',
    'company',
  ]
  const pulledOutData = {}
  pulledOutDataKeys.forEach((key) => {
    if (userData[key]) pulledOutData[`latest_${key}`] = userData[key]
  })

  const data = {
    latest_flow_id: self.idValue,
    ...pulledOutData,
    merged_data: JSON.stringify(userData),
    all_data: JSON.stringify(allData),
  }

  //LUCIAN added this for debouncing the jitsu data events
  if (bypassDebounce) jitsu.identify(data)
  else jitsuDebouncer(data)
  //End of LUCIANs addition
  // jitsu.track('data_updated')

  if (TEST_MODE) return
  trackUserData(self.idValue, userData)
}

// async function writeToFirebase(self: any, userData: Record<string, unknown>, userIds: string[]) {
//   if (TEST_MODE) return
//   const rawData: {
//     updatedAt: Transform
//     userId: string
//     userIds: string[]
//     submitted_timestamps?: Transform
//   } = {
//     userId: userIds[0],
//     userIds,
//     ...userData,
//     updatedAt: new Transform('serverTimestamp'),
//   }

//   if (userData.submitted_timestamps)
//     rawData.submitted_timestamps = new Transform(
//       'appendToArray',
//       userData.submitted_timestamps as number
//     )

//   const data = cleanObj(cloneDeep(rawData))

//   // console.log(
//   //   `User Data Document Write [Set]:`,
//   //   `forms/${self.idValue}/entries/${self.entryId}`,
//   //   data
//   // )
//   try {
//     await db.ref(`forms/${self.idValue}/entries/${self.entryId}`).update(data)
//   } catch (e) {
//     const errorIsExpected =
//       e && e.message && e.message.indexOf && e.message.startsWith(`No document to update`) !== -1

//     let baseErrorMessage = `Saving to Entries`
//     if (!errorIsExpected) {
//       if (TEST_MODE) console.error(e)
//       else {
//         try {
//           notify(
//             self,
//             `${baseErrorMessage} attempt update data: ${
//               e.response && e.response.data ? JSON.stringify(e.response.data) : e.message
//             } ${e}`,
//             null
//           )
//         } catch (error) {
//           console.error(error)
//         }
//       }
//     }

//     let attemptSet = true
//     if (attemptSet) {
//       const refData = { ...data }
//       if (!refData.createdAt) refData.createdAt = new Transform('serverTimestamp')
//       db.ref(`forms/${self.idValue}/entries/${self.entryId}`)
//         .set(refData)
//         .catch(async (err) => {
//           if (TEST_MODE) console.error(err)
//           try {
//             const message = `${baseErrorMessage} attempt set data: ${err.message}`
//             if (err.message.includes('Cannot convert firestore.v1.Value with type unset')) {
//               const replacer = (k: string, v: any) => {
//                 if (typeof v === 'number' && isNaN(v)) return 'NaN'
//                 if (v === Infinity) return 'Infinity'
//                 if (v === undefined) return 'Undefined'
//                 if (v && typeof v.then === 'function') return '[Pending Promise]'
//                 if (typeof v === 'function') return 'Function'
//                 if (typeof v === 'symbol') return 'Symbol'
//                 return v
//               }
//               const jsonData = JSON.stringify(userData, replacer)
//               notify(self, `Unset error.\n\n\`${jsonData}\``, null)
//             } else {
//               notify(self, message, null)
//             }
//           } catch (error) {
//             console.error(error)
//           }
//         })
//     }
//   }
// }

// function cleanObj(obj: object, useReplacer?: boolean) {
//   if (!obj) return obj
//   function replacer(k: string, v: any) {
//     if (['updatedAt', 'createdAt'].includes(k)) return v
//     if (typeof v === 'number' && isNaN(v)) return '_error'
//     if (v === Infinity) return '_error'
//     if (v === undefined) return null
//     if (v && typeof v.then === 'function') return '[Pending Promise]'
//     if (typeof v === 'function') return 'Function'
//     if (typeof v === 'symbol') return 'Symbol'
//     return v
//   }
//   Object.keys(obj).forEach((key) => {
//     const newKey = key || `_${key}`
//     if (!key) {
//       obj[newKey] = obj[key]
//       delete obj[key]
//     }
//     if (typeof obj[newKey] === 'number' && (isNaN(obj[newKey]) || obj[newKey] === Infinity)) {
//       obj[newKey] = '_error'
//     } else if (obj[newKey] && obj[newKey].then && typeof obj[newKey].then === 'function')
//       obj[newKey] = '[Pending Promise]'
//     else if (obj[newKey] && Array.isArray(obj[newKey])) {
//       obj[newKey].forEach((k: string, i: number) => {
//         if (Array.isArray(k)) obj[newKey][i] = JSON.stringify(k)
//         else if (
//           (typeof obj[newKey][i] === 'number' && isNaN(obj[newKey][i])) ||
//           obj[newKey] === Infinity
//         )
//           obj[newKey][i] = '_error'
//         else if (typeof k === 'object') obj[newKey][i] = cleanObj(k)
//         else if (k === undefined) obj[newKey][i] = null
//       })
//     } else if (obj[newKey] && typeof obj[newKey] === 'object') cleanObj(obj[newKey])
//     // @TODO make work with arrays
//     else if (obj[newKey] === undefined) obj[newKey] = null
//   })
//   return useReplacer ? JSON.parse(JSON.stringify(obj, replacer)) : obj
// }

function storageAvailable(type = 'localStorage') {
  let storage
  try {
    storage = window[type]
    var x = '__storage_test__'
    storage.setItem(x, x)
    storage.removeItem(x)
    return true
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    )
  }
}

interface C {
  key: never
  doNotSave: string
}

function prepareUserData(self: any, userData: Record<string, unknown>) {
  const keysToDeleteFirebase: string[] = []
  const keysToDeleteLocalStorage: string[] = []
  //LUCIAN added next if
  //Global components
  if (self.flow && Array.isArray(self.flow.components)) {
    self.flow.components.forEach((c: C) => {
      if (c.doNotSave === 'cloud') keysToDeleteFirebase.push(c.key)
      else if (c.doNotSave) {
        keysToDeleteFirebase.push(c.key)
        keysToDeleteLocalStorage.push(c.key)
      }
    })
  }
  //Registered keys
  if (self.flow && self.flow.registered_keys) {
    var keys = Array.isArray(self.flow.registered_keys)
      ? self.flow.registered_keys
      : self.flow.registered_keys.split('\n')
    keys.forEach((k) => {
      var regKey = k.split(':')[0]
      var regValue = k.split(':')[1] || 'local_cloud'
      if (regValue === 'none') {
        keysToDeleteFirebase.push(regKey)
        keysToDeleteLocalStorage.push(regKey)
      } else if (regValue === 'local') {
        keysToDeleteFirebase.push(regKey)
      }
    })
  }
  //END of LUCIANs addition
  if (self.flow && Array.isArray(self.flow.pages)) {
    self.flow.pages.forEach((p) => {
      if (Array.isArray(p.components))
        p.components.forEach((c: C) => {
          if (c.doNotSave === 'cloud') keysToDeleteFirebase.push(c.key)
          else if (c.doNotSave) {
            keysToDeleteFirebase.push(c.key)
            keysToDeleteLocalStorage.push(c.key)
          }
        })
    })
  }
  if (self.flow && Array.isArray(self.flow.computedFields)) {
    self.flow.computedFields.forEach((c: C) => {
      if (c.doNotSave === 'cloud') keysToDeleteFirebase.push(c.key)
      else if (c.doNotSave) {
        keysToDeleteFirebase.push(c.key)
        keysToDeleteLocalStorage.push(c.key)
      }
    })
  }

  const cloneTrim = (data) =>
    Object.entries(cloneDeep(data)).reduce((acc, [k, v]) => {
      acc[k] = typeof v === 'string' ? v.trim() : v
      return acc
    }, {})
  const localStorageData = cloneTrim(userData)
  const firebaseData = cloneTrim(userData)

  keysToDeleteFirebase.forEach((k) => delete firebaseData[k])
  keysToDeleteLocalStorage.forEach((k) => delete localStorageData[k])
  return { local: localStorageData, firebase: firebaseData }
}
